import { useState, useEffect } from "react";
import { II } from "../../components/common/Icons";
import PMModal from "./PMModal";
import { usePMData } from "../../context/PMContext";
import api from "../../services/api";import "../../assets/styles/PM/PMAnnouncePg.css";

function PMAnnouncePg() {
  const {
    pmAnnouncements,
    pmProjects,
    createAnnouncement,
    refreshPMData,
    loading
  } = usePMData();

  const [announcements, setAnnouncements] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterProject, setFilterProject] = useState("all");

  // Create Announcement Form
  const [form, setForm] = useState({
    title: "",
    body: "",
    type: "meeting",
    audience: "project",
    projectId: "",
    date: "",
    time: ""
  });

  // Edit Announcement Form
  const [editForm, setEditForm] = useState({
    id: "",
    title: "",
    body: "",
    type: "",
    audience: "",
    projectId: "",
    date: "",
    time: ""
  });

  // ✅ FIX 1: Force announcements to load from context
  useEffect(() => {
    console.log("📢 PMAnnouncePg mounted - loading announcements");
    if (pmAnnouncements && pmAnnouncements.length > 0) {
      console.log("✅ Announcements loaded from context:", pmAnnouncements.length);
      setAnnouncements(pmAnnouncements);
    } else {
      console.log("⚠️ No announcements in context, fetching directly...");
      fetchAnnouncementsDirectly();
    }
  }, [pmAnnouncements]);

  // ✅ FIX 2: Direct API call to ensure data loads
  const fetchAnnouncementsDirectly = async () => {
    try {
      const token = api.getToken();
      const response = await fetch('/api/pm/announcements', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        console.log("✅ Direct fetch got announcements:", data?.length || 0);
        setAnnouncements(data || []);
      }
    } catch (error) {
      console.error("❌ Direct fetch failed:", error);
    }
  };

  // ✅ FIX 3: Set default project when projects load
  useEffect(() => {
    if (pmProjects && pmProjects.length > 0 && !form.projectId) {
      setForm((prev) => ({ ...prev, projectId: pmProjects[0]?.pid || "" }));
    }
  }, [pmProjects]);

  // Reset create form
  const resetForm = () => {
    setForm({
      title: "",
      body: "",
      type: "meeting",
      audience: "project",
      projectId: pmProjects[0]?.pid || "",
      date: "",
      time: ""
    });
  };

  // Reset edit form
  const resetEditForm = () => {
    setEditForm({
      id: "",
      title: "",
      body: "",
      type: "",
      audience: "",
      projectId: "",
      date: "",
      time: ""
    });
  };

  // ✅ FIX 4: Create announcement with proper error handling
  const handleCreateAnnouncement = async () => {
    if (!form.title.trim()) {
      alert("❌ Title is required");
      return;
    }
    if (!form.body.trim()) {
      alert("❌ Content is required");
      return;
    }
    if (form.audience === "project" && !form.projectId) {
      alert("❌ Please select a project");
      return;
    }

    try {
      console.log("📝 Creating announcement:", form);
      await createAnnouncement({
        title: form.title.trim(),
        body: form.body.trim(),
        type: form.type,
        audience: form.audience,
        projectId: form.audience === "project" ? form.projectId : null,
        date: form.date || null,
        time: form.time || null
      });

      setSuccessMsg("✅ Announcement posted successfully!");
      setShowCreateModal(false);
      resetForm();
      await refreshPMData();
      await fetchAnnouncementsDirectly();

      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (error) {
      console.error("❌ Error creating announcement:", error);
      alert("Failed to create announcement: " + error.message);
    }
  };

  // ✅ FIX 5: Edit announcement
  const handleEditAnnouncement = async () => {
    if (!editForm.title.trim() || !editForm.body.trim()) {
      alert("Title and content are required");
      return;
    }

    try {
      const token = api.getToken();
      const response = await fetch(`/api/pm/announcements/${editForm.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: editForm.title,
          body: editForm.body,
          type: editForm.type,
          audience: editForm.audience,
          projectId: editForm.projectId,
          date: editForm.date,
          time: editForm.time
        })
      });

      if (response.ok) {
        setSuccessMsg("✅ Announcement updated successfully!");
        setShowEditModal(false);
        resetEditForm();
        await refreshPMData();
        await fetchAnnouncementsDirectly();
        setTimeout(() => setSuccessMsg(""), 4000);
      } else {
        throw new Error("Failed to update");
      }
    } catch (error) {
      console.error("❌ Error editing announcement:", error);
      alert("Failed to update announcement");
    }
  };

  // ✅ FIX 6: Delete announcement
  const handleDeleteAnnouncement = async () => {
    if (!selectedAnnouncement) return;
    const announcementId = selectedAnnouncement.id || selectedAnnouncement.ann_id;
    if (!announcementId) {
      alert("Invalid announcement id");
      return;
    }

    try {
      const token = api.getToken();
      const response = await fetch(`/api/pm/announcements/${announcementId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSuccessMsg("✅ Announcement deleted successfully!");
        setShowDeleteModal(false);
        setSelectedAnnouncement(null);
        await refreshPMData();
        await fetchAnnouncementsDirectly();
        setTimeout(() => setSuccessMsg(""), 4000);
      } else {
        throw new Error("Failed to delete");
      }
    } catch (error) {
      console.error("❌ Error deleting announcement:", error);
      alert("Failed to delete announcement");
    }
  };

  // View announcement details
  const viewAnnouncement = (ann) => {
    setSelectedAnnouncement(ann);
    setShowDetailModal(true);
  };

  // Edit announcement
  const editAnnouncement = (ann) => {
    setEditForm({
      id: ann.id || ann.ann_id,
      title: ann.title,
      body: ann.body,
      type: ann.type,
      audience: ann.audience || ann.scope,
      projectId: ann.project,
      date: ann.date,
      time: ann.time || ""
    });
    setShowEditModal(true);
    setShowDetailModal(false);
  };

  // Delete announcement
  const deleteAnnouncement = (ann) => {
    setSelectedAnnouncement(ann);
    setShowDeleteModal(true);
    setShowDetailModal(false);
  };

  // ✅ FIX 7: Filter announcements
  const filteredAnnouncements = announcements.filter((ann) => {
    const matchesSearch = !searchTerm ||
    ann.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ann.body?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ann.created_by?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = filterType === "all" || ann.type === filterType;

    const matchesProject = filterProject === "all" || ann.project === filterProject;

    return matchesSearch && matchesType && matchesProject;
  });

  // Get unique projects for filter
  const uniqueProjects = [...new Map(
    announcements.
    filter((a) => a.project && a.project_name).
    map((a) => [a.project, { id: a.project, name: a.project_name }])
  ).values()];

  // Get announcement counts
  const counts = {
    total: announcements.length,
    meetings: announcements.filter((a) => a.type === 'meeting').length,
    milestones: announcements.filter((a) => a.type === 'milestone').length,
    updates: announcements.filter((a) => a.type === 'update' || a.type === 'general').length
  };

  // ✅ FIX 8: If still loading and no announcements, show loading
  if (loading && announcements.length === 0) {
    return (
      <div className="pmannouncepg-inline-1">
        <div className="ob-pending-dot pmannouncepg-inline-2"></div>
        <div className="pmannouncepg-inline-3">Loading announcements...</div>
      </div>);

  }

  return (
    <div>
      {/* ✅ FIX 9: This is NOT the dashboard - it's Announcements Management */}
      <div className="pmannouncepg-inline-4">




        
        <div>
          <h1 className="pmannouncepg-inline-5">





            
            📢 Announcements
          </h1>
          <p className="pmannouncepg-inline-6">
            Manage and communicate with your project teams
          </p>
        </div>
        <div className="pmannouncepg-inline-7">
          <div className="pmannouncepg-inline-8">








            
            {II("search", 18, { c: "#94a3b8" })}
            <input








              placeholder="Search announcements..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} className="pmannouncepg-inline-9" />
            
          </div>
          <button
            className="btn pmannouncepg-inline-10"
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}>

            
            {II("plus", 16)} New Announcement
          </button>
        </div>
      </div>

      {/* ✅ FIX 10: Stats Cards - ANNOUNCEMENT stats, NOT project stats */}
      <div className="pmannouncepg-inline-11">




        
        <div className="cd pmannouncepg-inline-12">
          <div className="pmannouncepg-inline-13">📊 Total Announcements</div>
          <div className="pmannouncepg-inline-14">{counts.total}</div>
          <div className="pmannouncepg-inline-15">All time</div>
        </div>
        <div className="cd pmannouncepg-inline-16">
          <div className="pmannouncepg-inline-17">📅 Meetings</div>
          <div className="pmannouncepg-inline-18">{counts.meetings}</div>
          <div className="pmannouncepg-inline-19">Scheduled</div>
        </div>
        <div className="cd pmannouncepg-inline-20">
          <div className="pmannouncepg-inline-21">🏆 Milestones</div>
          <div className="pmannouncepg-inline-22">{counts.milestones}</div>
          <div className="pmannouncepg-inline-23">Achievements</div>
        </div>
        <div className="cd pmannouncepg-inline-24">
          <div className="pmannouncepg-inline-25">📋 Updates</div>
          <div className="pmannouncepg-inline-26">{counts.updates}</div>
          <div className="pmannouncepg-inline-27">General</div>
        </div>
      </div>

      {/* ✅ FIX 11: Filter Bar */}
      <div className="pmannouncepg-inline-28">









        
        <span className="pmannouncepg-inline-29">Filter by:</span>
        <div className="pmannouncepg-inline-30">
          <button
            className={`${`chip ${filterType === "all" ? "chip-p" : "chip-wn"}`} pmannouncepg-dyn-9`}
            onClick={() => setFilterType("all")}>
            
            
            All
          </button>
          <button
            className={`${`chip ${filterType === "meeting" ? "chip-ip" : "chip-wn"}`} pmannouncepg-dyn-10`}
            onClick={() => setFilterType("meeting")}>
            
            
            Meetings
          </button>
          <button
            className={`${`chip ${filterType === "milestone" ? "chip-p" : "chip-wn"}`} pmannouncepg-dyn-11`}
            onClick={() => setFilterType("milestone")}>
            
            
            Milestones
          </button>
          <button
            className={`${`chip ${filterType === "update" ? "chip-ok" : "chip-wn"}`} pmannouncepg-dyn-12`}
            onClick={() => setFilterType("update")}>
            
            
            Updates
          </button>
          <button
            className={`${`chip ${filterType === "general" ? "chip-wn" : "chip-wn"}`} pmannouncepg-dyn-13`}
            onClick={() => setFilterType("general")}>
            
            
            General
          </button>
        </div>

        {uniqueProjects.length > 0 &&
        <div className="pmannouncepg-inline-36">
            <span className="pmannouncepg-inline-37">Project:</span>
            <select
            className="fsel pmannouncepg-inline-38"

            value={filterProject}
            onChange={(e) => setFilterProject(e.target.value)}>
            
              <option value="all">All Projects</option>
              {uniqueProjects.map((p) =>
            <option key={p.id} value={p.id}>{p.name}</option>
            )}
            </select>
          </div>
        }
      </div>

      {/* Success Toast */}
      {successMsg &&
      <div className="toast pmannouncepg-inline-39">




        
          {II("chk", 18, { c: "#2563eb" })} {successMsg}
        </div>
      }

      {/* ✅ FIX 12: Announcements List - NOT DASHBOARD */}
      {filteredAnnouncements.length === 0 ?
      <div className="cd pmannouncepg-inline-40">





        
          <div className="pmannouncepg-inline-41">📢</div>
          <h2 className="pmannouncepg-inline-42">
            {searchTerm || filterType !== "all" || filterProject !== "all" ?
          'No matching announcements' :
          'No announcements yet'}
          </h2>
          <p className="pmannouncepg-inline-43">
            {searchTerm || filterType !== "all" || filterProject !== "all" ?
          'Try adjusting your search or filters' :
          'Create your first announcement to keep your team informed about meetings, milestones, and project updates.'
          }
          </p>
          {!searchTerm && filterType === "all" && filterProject === "all" &&
        <button
          className="btn pmannouncepg-inline-44"
          onClick={() => setShowCreateModal(true)}>

          
              {II("plus", 16)} Create Your First Announcement
            </button>
        }
        </div> :

      <div className="pmannouncepg-inline-45">
          {filteredAnnouncements.map((ann) => {
          const projectName = pmProjects.find((p) => p.pid === ann.project)?.name || ann.project_name || ann.project || "All Employees";
          const date = ann.date ? new Date(ann.date).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          }) : 'No date';

          return (
            <div key={ann.id} className="cd pmannouncepg-inline-46">






              
                {/* Header */}
                <div className="pmannouncepg-inline-47">






                
                  <div className="pmannouncepg-inline-48">
                    <div style={{ "--pmannouncepg-6-background":



                    ann.type === "meeting" ? "#e0f2fe" :
                    ann.type === "milestone" ? "#dbeafe" :
                    ann.type === "update" ? "#fff3cd" : "#f1f5f9" }} className="pmannouncepg-dyn-6">




                    
                      {ann.type === "meeting" ? "📅" :
                    ann.type === "milestone" ? "🏆" :
                    ann.type === "update" ? "📋" : "📢"}
                    </div>
                    <div>
                      <div className="pmannouncepg-inline-49">
                        {ann.title}
                      </div>
                      <div className="pmannouncepg-inline-50">
                        <span>📁 {projectName}</span>
                        <span>📅 {date}{ann.time ? ` at ${ann.time}` : ''}</span>
                      </div>
                    </div>
                  </div>
                  <span className={`${"chip " + (
                ann.type === "meeting" ? "chip-ip" :
                ann.type === "milestone" ? "chip-p" :
                ann.type === "update" ? "chip-ok" :
                "chip-wn")} pmannouncepg-dyn-14`}>
                  
                    {ann.type}
                  </span>
                </div>

                {/* Content Preview */}
                <div className="pmannouncepg-inline-52">
                  <div className="pmannouncepg-inline-53">









                  
                    {ann.body}
                  </div>
                  
                  {/* Footer */}
                  <div className="pmannouncepg-inline-54">






                  
                    <div className="pmannouncepg-inline-55">
                      {ann.source_role === 'itadmin' ? (
                        <span style={{ display:"inline-flex", alignItems:"center", gap:4, background:"#eff6ff", color:"#1d4ed8", padding:"2px 8px", borderRadius:20, fontSize:11, fontWeight:700, marginRight:6 }}>
                          🖥️ IT Admin
                        </span>
                      ) : null}
                      {II("user", 12, { c: "#94a3b8" })} Posted by <span className="pmannouncepg-inline-56">{ann.created_by || 'PM'}</span>
                      <span className="pmannouncepg-inline-57">
                        {II("clock", 12, { c: "#94a3b8" })} {ann.created_at ? new Date(ann.created_at).toLocaleString('en-IN', {
                        hour: '2-digit',
                        minute: '2-digit',
                        day: 'numeric',
                        month: 'short'
                      }) : date}
                      </span>
                    </div>
                    <div className="pmannouncepg-inline-58">
                      <button
                      className="btn-o btn-s pmannouncepg-inline-59"
                      onClick={() => viewAnnouncement(ann)}>

                      
                        {II("eye", 14)} View
                      </button>
                      {ann.source_role !== 'itadmin' && <button
                      className="btn-o btn-s pmannouncepg-inline-60"
                      onClick={() => editAnnouncement(ann)}>

                      
                        {II("edit", 14)} Edit
                      </button>}
                      {ann.source_role !== "itadmin" && <button
                      className="btn-o btn-s pmannouncepg-inline-61"
                      onClick={() => deleteAnnouncement(ann)}>






                      
                        {II("trash", 14)} Delete
                      </button>}
                    </div>
                  </div>
                </div>
              </div>);

        })}
        </div>
      }

      {/* ========== CREATE MODAL ========== */}
      <PMModal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title="Create New Announcement"
        wide={true}>
        
        <div>
          <div className="fg">
            <label className="fl">Announcement Title *</label>
            <input
              className="fi"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Sprint Review Meeting, Q2 Milestone Achieved" />
            
          </div>

          <div className="fr2">
            <div className="fg">
              <label className="fl">Type</label>
              <select
                className="fsel"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}>
                
                <option value="meeting">📅 Team Meeting</option>
                <option value="milestone">🏆 Milestone</option>
                <option value="update">📋 Project Update</option>
                <option value="general">📢 General</option>
              </select>
            </div>
            <div className="fg">
              <label className="fl">Audience</label>
              <select
                className="fsel"
                value={form.audience}
                onChange={(e) => setForm({ ...form, audience: e.target.value })}>
                
                <option value="project">👥 Project Members</option>
                <option value="all">🏢 All Employees</option>
              </select>
            </div>
          </div>

          {form.audience === "project" &&
          <div className="fg">
              <label className="fl">Select Project *</label>
              <select
              className="fsel"
              value={form.projectId}
              onChange={(e) => setForm({ ...form, projectId: e.target.value })}>
              
                <option value="">Choose a project...</option>
                {pmProjects.map((p) =>
              <option key={p.pid} value={p.pid}>{p.name} ({p.pid})</option>
              )}
              </select>
            </div>
          }

          <div className="fr2">
            <div className="fg">
              <label className="fl">Event Date (Optional)</label>
              <input
                className="fi"
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })} />
              
            </div>
            <div className="fg">
              <label className="fl">Event Time (Optional)</label>
              <input
                className="fi"
                type="time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })} />
              
            </div>
          </div>

          <div className="fg">
            <label className="fl">Content *</label>
            <textarea
              className="fta pmannouncepg-inline-62"
              rows={8}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Write your announcement here... Include meeting agenda, milestone details, important updates, etc." />

            
          </div>

          <div className="pmannouncepg-inline-63">
            <button className="btn-o" onClick={() => {
              setShowCreateModal(false);
              resetForm();
            }}>Cancel</button>
            <button className="btn" onClick={handleCreateAnnouncement}>
              {II("chk", 16)} Publish Announcement
            </button>
          </div>
        </div>
      </PMModal>

      {/* ========== EDIT MODAL ========== */}
      <PMModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          resetEditForm();
        }}
        title="Edit Announcement"
        wide={true}>
        
        <div>
          <div className="fg">
            <label className="fl">Title *</label>
            <input
              className="fi"
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
            
          </div>

          <div className="fr2">
            <div className="fg">
              <label className="fl">Type</label>
              <select
                className="fsel"
                value={editForm.type}
                onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}>
                
                <option value="meeting">📅 Team Meeting</option>
                <option value="milestone">🏆 Milestone</option>
                <option value="update">📋 Project Update</option>
                <option value="general">📢 General</option>
              </select>
            </div>
            <div className="fg">
              <label className="fl">Audience</label>
              <select
                className="fsel"
                value={editForm.audience}
                onChange={(e) => setEditForm({ ...editForm, audience: e.target.value })}>
                
                <option value="project">👥 Project Members</option>
                <option value="all">🏢 All Employees</option>
              </select>
            </div>
          </div>

          {editForm.audience === "project" &&
          <div className="fg">
              <label className="fl">Project</label>
              <select
              className="fsel"
              value={editForm.projectId}
              onChange={(e) => setEditForm({ ...editForm, projectId: e.target.value })}>
              
                <option value="">Select a project...</option>
                {pmProjects.map((p) =>
              <option key={p.pid} value={p.pid}>{p.name} ({p.pid})</option>
              )}
              </select>
            </div>
          }

          <div className="fr2">
            <div className="fg">
              <label className="fl">Date</label>
              <input
                className="fi"
                type="date"
                value={editForm.date}
                onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} />
              
            </div>
            <div className="fg">
              <label className="fl">Time</label>
              <input
                className="fi"
                type="time"
                value={editForm.time}
                onChange={(e) => setEditForm({ ...editForm, time: e.target.value })} />
              
            </div>
          </div>

          <div className="fg">
            <label className="fl">Content *</label>
            <textarea
              className="fta pmannouncepg-inline-64"
              rows={8}
              value={editForm.body}
              onChange={(e) => setEditForm({ ...editForm, body: e.target.value })} />

            
          </div>

          <div className="pmannouncepg-inline-65">
            <button className="btn-o" onClick={() => {
              setShowEditModal(false);
              resetEditForm();
            }}>Cancel</button>
            <button className="btn" onClick={handleEditAnnouncement}>
              {II("chk", 16)} Update Announcement
            </button>
          </div>
        </div>
      </PMModal>

      {/* ========== DETAIL MODAL ========== */}
      <PMModal
        open={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedAnnouncement(null);
        }}
        title=""
        wide={true}>
        
        {selectedAnnouncement &&
        <div>
            <div className="pmannouncepg-inline-66">




            
              <div className="pmannouncepg-inline-67">
                <div style={{ "--pmannouncepg-8-background":



                selectedAnnouncement.type === "meeting" ? "#2563eb" :
                selectedAnnouncement.type === "milestone" ? "#2563eb" :
                selectedAnnouncement.type === "update" ? "#d97706" : "#64748b" }} className="pmannouncepg-dyn-8">





                
                  {selectedAnnouncement.type === "meeting" ? "📅" :
                selectedAnnouncement.type === "milestone" ? "🏆" :
                selectedAnnouncement.type === "update" ? "📋" : "📢"}
                </div>
                <div>
                  <h2 className="pmannouncepg-inline-68">
                    {selectedAnnouncement.title}
                  </h2>
                  <span className={`${"chip " + (
                selectedAnnouncement.type === "meeting" ? "chip-ip" :
                selectedAnnouncement.type === "milestone" ? "chip-p" :
                selectedAnnouncement.type === "update" ? "chip-ok" :
                "chip-wn")} pmannouncepg-dyn-15`}>
                  
                    {selectedAnnouncement.type}
                  </span>
                </div>
              </div>
              
              <div className="pmannouncepg-inline-70">







              
                <div>
                  <div className="pmannouncepg-inline-71">👥 Audience</div>
                  <div className="pmannouncepg-inline-72">
                    {selectedAnnouncement.audience === 'all' ? 'All Employees' :
                  pmProjects.find((p) => p.pid === selectedAnnouncement.project)?.name || selectedAnnouncement.project_name || selectedAnnouncement.project}
                  </div>
                </div>
                <div>
                  <div className="pmannouncepg-inline-73">📅 Date & Time</div>
                  <div className="pmannouncepg-inline-74">
                    {selectedAnnouncement.date || 'No date'} 
                    {selectedAnnouncement.time ? ` at ${selectedAnnouncement.time}` : ''}
                  </div>
                </div>
                <div>
                  <div className="pmannouncepg-inline-75">✍️ Posted By</div>
                  <div className="pmannouncepg-inline-76">{selectedAnnouncement.created_by || 'PM'}</div>
                </div>
                <div>
                  <div className="pmannouncepg-inline-77">🕐 Posted On</div>
                  <div className="pmannouncepg-inline-78">
                    {selectedAnnouncement.created_at ? new Date(selectedAnnouncement.created_at).toLocaleString('en-IN', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : selectedAnnouncement.date}
                  </div>
                </div>
              </div>
            </div>

            <div className="pmannouncepg-inline-79">
              <h3 className="pmannouncepg-inline-80">
                📄 Content
              </h3>
              <div className="pmannouncepg-inline-81">









              
                {selectedAnnouncement.body}
              </div>
            </div>

            <div className="pmannouncepg-inline-82">
              <button
              className="btn pmannouncepg-inline-83"

              onClick={() => editAnnouncement(selectedAnnouncement)}>
              
                {II("edit", 16)} Edit
              </button>
              <button
              className="btn pmannouncepg-inline-84"

              onClick={() => deleteAnnouncement(selectedAnnouncement)}>
              
                {II("trash", 16)} Delete
              </button>
              <button className="btn-o" onClick={() => setShowDetailModal(false)}>Close</button>
            </div>
          </div>
        }
      </PMModal>

      {/* ========== DELETE MODAL ========== */}
      <PMModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedAnnouncement(null);
        }}
        title="Delete Announcement">
        
        {selectedAnnouncement &&
        <div className="pmannouncepg-inline-85">
            <div className="pmannouncepg-inline-86">










            
              🗑️
            </div>
            <h3 className="pmannouncepg-inline-87">
              Delete Announcement?
            </h3>
            <p className="pmannouncepg-inline-88">
              Are you sure you want to delete <span className="pmannouncepg-inline-89">"{selectedAnnouncement.title}"</span>?
            </p>
            <div className="pmannouncepg-inline-90">







            
              ⚠️ This action cannot be undone. The announcement will be permanently removed.
            </div>
            
            <div className="pmannouncepg-inline-91">
              <button className="btn-o" onClick={() => {
              setShowDeleteModal(false);
              setSelectedAnnouncement(null);
            }}>Cancel</button>
              <button
              className="btn pmannouncepg-inline-92"

              onClick={handleDeleteAnnouncement}>
              
                {II("trash", 16)} Yes, Delete
              </button>
            </div>
          </div>
        }
      </PMModal>
    </div>);

}

export default PMAnnouncePg;